import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";

import { type AttachmentFile } from "./attachmentUtils";

type DropzoneSurfaceProps = {
    isDragging?: boolean;
    isDisabled?: boolean;
    onPress: () => void;
    remainingSlots: number;
};

export function DropzoneSurface({
    isDragging = false,
    isDisabled = false,
    onPress,
    remainingSlots,
}: DropzoneSurfaceProps) {
    const { t } = useTranslation();

    return (
        <Pressable
            onPress={onPress}
            disabled={isDisabled}
            className={cn(
                "items-center justify-center rounded-md border border-dashed px-4 py-8 active:opacity-80",
                isDragging
                    ? "border-primary bg-primary/5"
                    : "border-border bg-muted/30",
                isDisabled && "opacity-50",
            )}
        >
            <Icon
                name="cloud-arrow-up"
                className={cn(
                    isDragging ? "text-primary" : "text-muted-foreground",
                )}
                size={28}
            />
            <Text variant="label" className="mt-3 text-center">
                {isDragging
                    ? t("leave.dropzone.dragging")
                    : t("leave.dropzone.idle")}
            </Text>
        </Pressable>
    );
}

export function mergeAttachments(
    existing: AttachmentFile[],
    incoming: AttachmentFile[],
    maxFiles: number,
): AttachmentFile[] {
    const merged = [...existing];

    for (const file of incoming) {
        if (merged.length >= maxFiles) {
            break;
        }

        const isDuplicate = merged.some(
            (item) => item.name === file.name && item.size === file.size,
        );

        if (!isDuplicate) {
            merged.push(file);
        }
    }

    return merged;
}

export function revokeAttachmentUri(uri: string) {
    if (uri.startsWith("blob:")) {
        URL.revokeObjectURL(uri);
    }
}
